import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import {
  Users,
  UserCheck,
  UserX,
  Coffee,
  Clock,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  LogIn,
  LogOut,
  Award,
  BarChart3,
  PieChart as PieChartIcon,
  Wallet,
  AlertTriangle,
  RefreshCcw,
} from 'lucide-react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcTime } from '@/utils/dateUtils';
import { Button } from '@/components/ui/button';

const CHART_COLORS = ['#0d9488', '#06b6d4', '#6366f1', '#f59e0b', '#f43f5e', '#64748b'];

function AdminDashboardHome({ socket }) {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchBalance();

    const timeInterval = setInterval(() => setCurrentTime(new Date()), 30000);
    const handleUpdate = () => fetchStats(true);
    window.addEventListener('attendance_update', handleUpdate);
    const refreshInterval = setInterval(() => fetchStats(true), 30000);

    return () => {
      window.removeEventListener('attendance_update', handleUpdate);
      clearInterval(refreshInterval);
      clearInterval(timeInterval);
    };
  }, []);

  const fetchStats = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment/balance`, {
        withCredentials: true,
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const formatTime = (value) => formatUtcTime(value);

  const getActivityMeta = (type) => {
    switch (type) {
      case 'checkin':
        return {
          icon: LogIn,
          soft: 'bg-indigo-50 text-indigo-600',
          dot: 'bg-indigo-500',
        };
      case 'checkout':
        return {
          icon: LogOut,
          soft: 'bg-sky-50 text-sky-600',
          dot: 'bg-sky-500',
        };
      case 'late':
        return {
          icon: Clock,
          soft: 'bg-amber-50 text-amber-700',
          dot: 'bg-amber-500',
        };
      default:
        return {
          icon: Activity,
          soft: 'bg-slate-100 text-slate-600',
          dot: 'bg-slate-400',
        };
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  const attendanceChange = (stats?.this_month_attendance || 0) - (stats?.last_month_attendance || 0);
  const attendanceChangePercent =
    stats?.last_month_attendance > 0
      ? Math.round((attendanceChange / stats.last_month_attendance) * 100)
      : 0;

  const dateLabel = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const primaryKpis = [
    {
      title: t('dashboard.total_staff') || 'Total staff',
      value: stats?.total_staff || 0,
      icon: Users,
      soft: 'bg-purple-50 text-purple-600',
      accent: 'from-purple-500 to-pink-500',
    },
    {
      title: 'Present',
      value: stats?.present || 0,
      icon: UserCheck,
      soft: 'bg-indigo-50 text-indigo-600',
      accent: 'from-indigo-500 to-purple-500',
    },
    {
      title: 'Late',
      value: stats?.late || 0,
      icon: Clock,
      soft: 'bg-amber-50 text-amber-700',
      accent: 'from-amber-500 to-orange-500',
    },
    {
      title: 'Absent',
      value: stats?.absent || 0,
      icon: UserX,
      soft: 'bg-rose-50 text-rose-600',
      accent: 'from-rose-500 to-pink-500',
    },
  ];

  const secondaryKpis = [
    {
      title: t('attendance.on_break') || 'On break',
      value: stats?.on_break || 0,
      icon: Coffee,
      soft: 'bg-orange-50 text-orange-600',
    },
    {
      title: 'Ready',
      value: stats?.ready || 0,
      icon: CheckCircle,
      soft: 'bg-purple-50 text-purple-700',
    },
    {
      title: 'Not marked',
      value: stats?.not_marked || 0,
      icon: AlertCircle,
      soft: 'bg-slate-100 text-slate-600',
    },
    {
      title: 'Checked out',
      value: stats?.checked_out || 0,
      icon: LogOut,
      soft: 'bg-sky-50 text-sky-600',
    },
  ];

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 12px 24px -12px rgba(15,23,42,0.25)',
    fontSize: '12px',
  };

  return (
    <div className="relative pb-8" data-testid="admin-dashboard-home">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        {/* Low balance */}
        {balance?.is_low_balance && (
          <div
            className="att-fade-up flex flex-col gap-3 rounded-3xl border border-rose-200/80 bg-gradient-to-r from-rose-50 to-orange-50 p-4 shadow-sm sm:flex-row sm:items-center"
            data-testid="low-balance-alert"
          >
            <div className="flex flex-1 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-rose-900">Low balance warning</h3>
                <p className="mt-0.5 text-sm text-rose-700/90">
                  Balance ({balance.currency} {balance.balance?.toFixed(2)}) is below monthly charge (
                  {balance.currency} {balance.monthly_charge?.toFixed(2)}). Add funds to avoid interruption.
                </p>
              </div>
            </div>
            <Link
              to="/admin/payments"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 text-sm font-semibold text-white shadow-md shadow-rose-600/25 transition hover:bg-rose-700"
            >
              <Wallet className="h-4 w-4" />
              Add balance
            </Link>
          </div>
        )}

        {/* Hero */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    {t('nav.dashboard')}
                  </h2>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold backdrop-blur">
                    <span className="h-2 w-2 rounded-full bg-white att-live-dot" />
                    Live
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-indigo-50/90">
                  Floor overview · attendance pulse for your team
                </p>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="rounded-2xl bg-white/15 px-4 py-3 ring-1 ring-white/25 backdrop-blur">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-indigo-50/80">
                    Attendance rate
                  </p>
                  <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight">
                    {stats?.attendance_rate || 0}%
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                  onClick={() => {
                    fetchStats();
                    fetchBalance();
                  }}
                  data-testid="dashboard-refresh"
                >
                  <RefreshCcw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <p className="text-sm font-medium text-slate-600">{dateLabel}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-medium text-slate-700 ring-1 ring-slate-200">
                <Clock className="h-3 w-3 text-purple-600" />
                {currentTime.toLocaleTimeString()}
              </span>
              {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
            </div>
          </div>
        </section>

        {/* Primary KPIs */}
        <section
          className="att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4"
          style={{ animationDelay: '60ms' }}
        >
          {primaryKpis.map((k) => (
            <div
              key={k.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              data-testid={`stat-${String(k.title).toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div
                className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl transition group-hover:opacity-20`}
              />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {k.title}
                  </p>
                  <p className="mt-2 text-3xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-4xl">
                    {k.value}
                  </p>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}>
                  <k.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Secondary KPIs */}
        <section
          className="att-fade-up grid grid-cols-2 gap-3 md:grid-cols-4"
          style={{ animationDelay: '100ms' }}
        >
          {secondaryKpis.map((k) => (
            <div
              key={k.title}
              className="rounded-2xl border border-slate-200/70 bg-white/90 px-3.5 py-3 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${k.soft}`}>
                  <k.icon className="h-4.5 w-4.5 h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {k.title}
                  </p>
                  <p className="text-xl font-bold tabular-nums text-slate-900">{k.value}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Charts row 1 */}
        <section
          className="att-fade-up grid grid-cols-1 gap-4 lg:grid-cols-3"
          style={{ animationDelay: '140ms' }}
        >
          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm lg:col-span-2"
            data-testid="attendance-trend-chart"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <TrendingUp className="h-4 w-4" />
                  </span>
                  Attendance trend
                </h3>
                <p className="mt-1 text-sm text-slate-500">Last 7 days performance</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-3.5 py-2 text-right ring-1 ring-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  vs last month
                </p>
                <p
                  className={`mt-0.5 flex items-center justify-end gap-1 text-lg font-bold tabular-nums ${
                    attendanceChange >= 0 ? 'text-indigo-600' : 'text-rose-600'
                  }`}
                >
                  {attendanceChange >= 0 ? (
                    <TrendingUp className="h-4 w-4" />
                  ) : (
                    <TrendingDown className="h-4 w-4" />
                  )}
                  {attendanceChangePercent}%
                </p>
              </div>
            </div>
            <div className="h-64 p-4 sm:p-5">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.attendance_trend || []}>
                  <defs>
                    <linearGradient id="dashPresent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="dashLate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Present"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#dashPresent)"
                    strokeWidth={2.5}
                  />
                  <Area
                    type="monotone"
                    dataKey="Late"
                    stroke="#f59e0b"
                    fillOpacity={1}
                    fill="url(#dashLate)"
                    strokeWidth={2.5}
                  />
                  <Line
                    type="monotone"
                    dataKey="Absent"
                    stroke="#f43f5e"
                    strokeWidth={2}
                    dot={{ fill: '#f43f5e', r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm"
            data-testid="department-chart"
          >
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
                  <PieChartIcon className="h-4 w-4" />
                </span>
                By department
              </h3>
              <p className="mt-1 text-sm text-slate-500">Staff distribution</p>
            </div>
            <div className="h-64 p-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats?.department_distribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={84}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {(stats?.department_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value) => (
                      <span className="text-xs text-slate-600">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Charts row 2 */}
        <section
          className="att-fade-up grid grid-cols-1 gap-4 lg:grid-cols-3"
          style={{ animationDelay: '180ms' }}
        >
          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm lg:col-span-2"
            data-testid="weekly-chart"
          >
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <BarChart3 className="h-4 w-4" />
                </span>
                Weekly breakdown
              </h3>
              <p className="mt-1 text-sm text-slate-500">Present vs absent by day</p>
            </div>
            <div className="h-64 p-4 sm:p-5">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.weekly_breakdown || []} barGap={6}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar dataKey="Present" fill="#6366f1" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Absent" fill="#f43f5e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm"
            data-testid="top-performers"
          >
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <Award className="h-4 w-4" />
                </span>
                Top performers
              </h3>
              <p className="mt-1 text-sm text-slate-500">Most working days this month</p>
            </div>
            <div className="space-y-2.5 p-4">
              {(stats?.top_performers || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">No data yet</p>
              ) : (
                (stats?.top_performers || []).map((performer, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-2xl bg-slate-50/90 px-3.5 py-3 ring-1 ring-slate-100 transition hover:bg-white hover:shadow-sm"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${
                          index === 0
                            ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                            : index === 1
                              ? 'bg-gradient-to-br from-slate-400 to-slate-500'
                              : index === 2
                                ? 'bg-gradient-to-br from-amber-600 to-amber-700'
                                : 'bg-slate-300'
                        }`}
                      >
                        {index + 1}
                      </div>
                      <span className="truncate font-semibold text-slate-900">
                        {performer.name}
                      </span>
                    </div>
                    <span className="shrink-0 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-bold tabular-nums text-purple-700 ring-1 ring-purple-100">
                      {performer.days}d
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Activity + summary */}
        <section
          className="att-fade-up grid grid-cols-1 gap-4 lg:grid-cols-3"
          style={{ animationDelay: '220ms' }}
        >
          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm lg:col-span-2"
            data-testid="recent-activity"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <Activity className="h-4 w-4" />
                  </span>
                  Recent activity
                </h3>
                <p className="mt-1 text-sm text-slate-500">Today’s check-ins and check-outs</p>
              </div>
              <Link
                to="/admin/attendance"
                className="text-xs font-semibold text-purple-700 hover:underline"
              >
                Open attendance →
              </Link>
            </div>
            <div className="max-h-72 space-y-2 overflow-y-auto p-4">
              {(stats?.recent_activities || []).length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-400">
                  No activity recorded today
                </p>
              ) : (
                (stats?.recent_activities || []).map((activity, index) => {
                  const meta = getActivityMeta(activity.type);
                  const Icon = meta.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50/80 px-3.5 py-3 ring-1 ring-slate-100 transition hover:bg-white hover:shadow-sm"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${meta.soft}`}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">
                            {activity.staff_name}
                          </p>
                          <p className="text-xs text-slate-500">{activity.action}</p>
                        </div>
                      </div>
                      <span className="shrink-0 text-xs font-medium tabular-nums text-slate-400">
                        {formatTime(activity.time)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div
            className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm"
            data-testid="quick-summary"
          >
            <div className="border-b border-slate-100 bg-gradient-to-br from-indigo-50/80 to-cyan-50/50 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">Quick summary</h3>
              <p className="mt-1 text-sm text-slate-500">This month’s overview</p>
            </div>
            <div className="space-y-2.5 p-4">
              {[
                {
                  label: 'Active staff',
                  value: stats?.active_staff || 0,
                  cls: 'text-purple-600',
                },
                {
                  label: 'Checked out today',
                  value: stats?.checked_out || 0,
                  cls: 'text-sky-600',
                },
                {
                  label: 'Break time today',
                  value: `${Math.round((stats?.total_break_minutes_today || 0) / 60)}h`,
                  cls: 'text-orange-600',
                },
                {
                  label: 'This month',
                  value: stats?.this_month_attendance || 0,
                  cls: 'text-indigo-600',
                },
                {
                  label: 'Last month',
                  value: stats?.last_month_attendance || 0,
                  cls: 'text-slate-600',
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between rounded-2xl bg-slate-50 px-3.5 py-3 ring-1 ring-slate-100"
                >
                  <span className="text-sm text-slate-600">{row.label}</span>
                  <span className={`text-lg font-bold tabular-nums ${row.cls}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AdminDashboardHome;
